import type { DsTone } from 'src/auditoria/components/ds';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { anexarPlantao } from 'src/actions/anexos';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGridClient } from 'src/auditoria/components/tx-data-grid/TxDataGridClient';
import {
  DsNum,
  DsField,
  DsButton,
  colunaLink,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { SX_COLUNAS, AnexosCanvas, PainelConversa, TituloConversa } from '../Protocolo/canvas';

// ----------------------------------------------------------------------
// Plantão Fiscal → Solicitações (artboard `portal-plantao-fiscal`). Tickets de
// atendimento do contribuinte ao fisco. Backend: GET /portal-api/plantao-fiscal
// (lista), GET /portal-api/plantao-fiscal/:id (thread), POST da nova dúvida e
// POST /portal-api/plantao-fiscal/:id/mensagem. Assuntos em
// GET /portal-api/plantao-fiscal/assuntos. Ver SPEC-PORTAL-SN-TOMADOR-PF.md §9.
//
// O canvas põe a abertura num painel de 300px ao lado da grade, não num modal,
// e traz a conversa da solicitação selecionada para baixo da própria lista.
// ----------------------------------------------------------------------

// Situação da solicitação: o backend confirma apenas situacao = 1 (aberta / em
// análise pelo plantão). Códigos não confirmados continuam crus, como no resto
// do portal — não inventamos rótulo que o schema não garante.
const SITUACAO_ABERTA = 1;

function situacaoLabel(situacao: number | null): string {
  if (situacao === SITUACAO_ABERTA) return 'Em análise';
  return `Situação ${situacao ?? '—'}`;
}

/** `Em análise` em warning, como o artboard declara; o resto fica neutro. */
function toneSituacaoPlantao(situacao: number | null): DsTone {
  return situacao === SITUACAO_ABERTA ? 'warning' : 'default';
}

interface PlantaoSolicitacao {
  id: number;
  protocolo: string | null;
  titulo: string | null;
  assunto: string | null;
  situacao: number | null;
  data_abertura: string | null;
}

interface PlantaoResposta {
  solicitacoes: PlantaoSolicitacao[];
}

interface MensagemPlantao {
  id: number;
  mensagem: string | null;
  data_hora: string | null;
  do_contribuinte: boolean;
}

interface PlantaoDetalhe {
  solicitacao: {
    id: number;
    protocolo: string | null;
    titulo: string | null;
  } | null;
  mensagens: MensagemPlantao[];
}

interface Assunto {
  id: number;
  descricao: string;
}

export default function PlantaoFiscalListPage() {
  const navigate = useNavigate();
  const { data, isLoading, mutate } = useSWR<PlantaoResposta>(
    '/portal-api/plantao-fiscal',
    fetcher
  );
  const solicitacoes = data?.solicitacoes ?? [];

  // --- conversa da solicitação selecionada --------------------------------
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const aberta = selecionada ?? solicitacoes[0]?.id ?? null;

  const { data: detalhe, mutate: recarregarThread } = useSWR<PlantaoDetalhe>(
    aberta ? `/portal-api/plantao-fiscal/${aberta}` : null,
    fetcher
  );

  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);

  const enviarMensagem = async () => {
    const texto = novaMensagem.trim();
    if (!texto || !aberta) return;
    setEnviandoMensagem(true);
    try {
      await axiosInstance.post(`/portal-api/plantao-fiscal/${aberta}/mensagem`, {
        mensagem: texto,
      });
      setNovaMensagem('');
      await recarregarThread();
    } catch {
      toast.error('Falha ao enviar a mensagem.');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  // --- painel de 300px "Nova dúvida" --------------------------------------
  const { data: assuntosData } = useSWR<{ assuntos: Assunto[] }>(
    '/portal-api/plantao-fiscal/assuntos',
    fetcher
  );
  const assuntos = assuntosData?.assuntos ?? [];

  const campoAssunto = useRef<HTMLInputElement>(null);
  const [fkassunto, setFkassunto] = useState<number | ''>('');
  const [titulo, setTitulo] = useState('');
  const [anotacoes, setAnotacoes] = useState('');
  const [anexos, setAnexos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    if (!titulo.trim() || fkassunto === '') {
      toast.error('Informe o assunto e o título.');
      return;
    }
    setEnviando(true);
    try {
      const res = await axiosInstance.post('/portal-api/plantao-fiscal', {
        fkassunto,
        titulo: titulo.trim(),
        anotacoes: anotacoes.trim() || undefined,
      });
      const novaId: number | undefined = res.data?.id;
      toast.success(`Solicitação aberta: ${res.data?.protocolo ?? ''}`);

      // Anexa os arquivos à solicitação recém-criada (base64 → bytea).
      if (novaId && anexos.length) {
        const falhas = await anexarPlantao(novaId, anexos);
        if (falhas > 0)
          toast.error(`${falhas} anexo(s) não puderam ser enviados. Reenvie na solicitação.`);
      }

      setFkassunto('');
      setTitulo('');
      setAnotacoes('');
      setAnexos([]);
      await mutate();
      if (novaId) setSelecionada(novaId);
    } catch {
      toast.error('Falha ao abrir a solicitação.');
    } finally {
      setEnviando(false);
    }
  };

  // Colunas do canvas: Protocolo / Título / Assunto / Situação, em
  // 120 / 1fr / 170 / 130. A data de abertura sai da grade (fica no detalhe).
  const colunas: GridColDef<PlantaoSolicitacao>[] = [
    {
      field: 'protocolo',
      headerName: 'Protocolo',
      width: 120,
      renderCell: (p) => <DsNum mono>{p.row.protocolo ?? '—'}</DsNum>,
    },
    colunaLink({ field: 'titulo', headerName: 'Título', flex: 1, minWidth: 220 }, (row) =>
      setSelecionada(row.id)
    ),
    { field: 'assunto', headerName: 'Assunto', width: 170 },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 130,
      renderCell: (p) => (
        <DsStatusChip tone={toneSituacaoPlantao(p.row.situacao)}>
          {situacaoLabel(p.row.situacao)}
        </DsStatusChip>
      ),
    },
  ];

  const solicitacaoAberta = solicitacoes.find((s) => s.id === aberta);

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Plantão Fiscal"
        subtitle="Dúvidas e orientações do fisco · conversa por protocolo."
        action={
          <DsButton
            tone="primary"
            size="tall"
            icon="mais"
            onClick={() => campoAssunto.current?.focus()}
          >
            Nova dúvida
          </DsButton>
        }
      />

      <Box sx={SX_COLUNAS}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GradeCanvas>
            <TxDataGridClient<PlantaoSolicitacao>
              rows={solicitacoes}
              columns={colunas}
              getRowId={(row) => row.id}
              autoHeight
              hideToolbar
              hideFooter
              // Sem rodapé não há como paginar — ver o mesmo ajuste no DTE.
              initialPageSize={100}
              localeText={{
                noRowsLabel: isLoading
                  ? 'Carregando solicitações…'
                  : 'Nenhuma solicitação aberta no plantão.',
              }}
              {...SX_GRADE_CANVAS}
            />
          </GradeCanvas>

          {aberta && (
            <PainelConversa
              titulo={
                <TituloConversa
                  numero={
                    solicitacaoAberta?.protocolo ?? detalhe?.solicitacao?.protocolo ?? `#${aberta}`
                  }
                  assunto={solicitacaoAberta?.titulo ?? detalhe?.solicitacao?.titulo ?? null}
                  aoAbrir={() => navigate(`/plantao-fiscal/${aberta}`)}
                />
              }
              mensagens={(detalhe?.mensagens ?? []).map((m) => ({
                id: m.id,
                texto: m.mensagem ?? '',
                doContribuinte: m.do_contribuinte,
              }))}
              vazio="Ainda não há mensagens nesta solicitação."
              placeholder="Escreva uma mensagem…"
              valor={novaMensagem}
              onChange={setNovaMensagem}
              onEnviar={enviarMensagem}
              enviando={enviandoMensagem}
            />
          )}
        </Box>

        <Card sx={{ flex: '0 0 300px', padding: '18px 20px' }}>
          <Box sx={{ fontSize: 13, fontWeight: 800, mb: '10px' }}>Nova dúvida</Box>

          <DsField label="Assunto" density="sm" required sx={{ mb: '10px' }}>
            <TextField
              select
              fullWidth
              inputRef={campoAssunto}
              value={fkassunto}
              onChange={(e) => setFkassunto(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {assuntos.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.descricao}
                </MenuItem>
              ))}
            </TextField>
          </DsField>

          <DsField label="Título" density="sm" required sx={{ mb: '10px' }}>
            <TextField fullWidth value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </DsField>

          <DsField label="Descrição / anotações" density="sm" sx={{ mb: '10px' }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={anotacoes}
              onChange={(e) => setAnotacoes(e.target.value)}
            />
          </DsField>

          <DsField label="Anexos" density="sm" sx={{ mb: '10px' }}>
            <AnexosCanvas arquivos={anexos} onChange={setAnexos} disabled={enviando} />
          </DsField>

          <DsButton
            tone="primary"
            size="tall"
            disabled={enviando}
            onClick={enviar}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            Enviar ao plantão
          </DsButton>
        </Card>
      </Box>
    </DashboardContent>
  );
}
